import { useState, FormEvent, useRef, useEffect } from 'react';
import { getSupabase } from '../../../../lib/supabase';
import { PHONE_DISPLAY } from '../../../../lib/businessInfo';

export default function RequestForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleType: '',
    make: '',
    model: '',
    yearMin: '',
    yearMax: '',
    budgetMin: '',
    budgetMax: '',
    timeline: '',
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Move focus to success heading when success state renders
  useEffect(() => {
    if (submitStatus === 'success') {
      requestAnimationFrame(() => successHeadingRef.current?.focus());
    }
  }, [submitStatus]);

  // Move focus to error live region when error state is set
  useEffect(() => {
    if (submitStatus === 'error') {
      requestAnimationFrame(() => errorRef.current?.focus());
    }
  }, [submitStatus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Basic required-field validation with focus recovery
    const requiredFields: Array<keyof typeof formData> = ['name', 'email', 'phone', 'vehicleType', 'budgetMin', 'budgetMax', 'timeline'];
    for (const field of requiredFields) {
      if (!formData[field].trim()) {
        const el = formRef.current?.querySelector<HTMLElement>(`#req-${field}`);
        if (el) el.focus();
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const client = await getSupabase();
      const { error } = await client.functions.invoke('broker-inquiry', {
        body: {
          ...formData,
          source: 'Request a Vehicle Page'
        }
      });

      if (error) throw error;

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicleType: '',
        make: '',
        model: '',
        yearMin: '',
        yearMax: '',
        budgetMin: '',
        budgetMax: '',
        timeline: '',
        notes: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h3
              ref={successHeadingRef}
              tabIndex={-1}
              className="text-2xl font-bold text-gray-900 mb-3 focus:outline-none"
            >
              Request Received!
            </h3>
            <p className="text-gray-700 mb-6">
              Thanks for reaching out! We'll start hunting for your perfect vehicle and get back to you within 24 hours with options.
            </p>
            <button
              onClick={() => setSubmitStatus('idle')}
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors whitespace-nowrap"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Request Your Vehicle
          </h2>
          <p className="text-lg text-gray-600">
            Fill out the form below and we'll start hunting. The more details you provide, the better we can match your needs.
          </p>
        </div>

        <form id="broker-request-vehicle-form" data-readdy-form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-8 shadow-sm" ref={formRef}>
          {/* Submit error */}
          {submitStatus === 'error' && (
            <div
              ref={errorRef}
              tabIndex={-1}
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className="flex items-start gap-3 p-4 mb-6 bg-red-50 border border-red-200 rounded-lg focus:outline-none"
            >
              <i className="ri-error-warning-line text-red-600 mt-0.5 flex-shrink-0" aria-hidden="true"></i>
              <p className="text-sm text-red-800">
                Something went wrong. Please try again or call us at {PHONE_DISPLAY}.
              </p>
            </div>
          )}

          {/* Contact Information */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-user-line text-amber-500" aria-hidden="true"></i>
              Contact Information
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="req-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="req-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  autoComplete="name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label htmlFor="req-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span aria-hidden="true">*</span>
                </label>
                <input
                  type="email"
                  id="req-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  autoComplete="email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="req-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number <span aria-hidden="true">*</span>
              </label>
              <input
                type="tel"
                id="req-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                aria-required="true"
                autoComplete="tel"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                placeholder="(503) 555-0123"
              />
            </div>
          </div>

          {/* Vehicle Preferences */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-car-line text-amber-500" aria-hidden="true"></i>
              Vehicle Preferences
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="req-vehicleType" className="block text-sm font-semibold text-gray-700 mb-2">
                  Vehicle Type <span aria-hidden="true">*</span>
                </label>
                <select
                  id="req-vehicleType"
                  name="vehicleType"
                  value={formData.vehicleType}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                >
                  <option value="">Select type...</option>
                  <option value="Sedan">Sedan</option>
                  <option value="SUV">SUV</option>
                  <option value="Truck">Truck</option>
                  <option value="Coupe">Coupe</option>
                  <option value="Hatchback">Hatchback</option>
                  <option value="Wagon">Wagon</option>
                  <option value="Van/Minivan">Van/Minivan</option>
                  <option value="Convertible">Convertible</option>
                  <option value="Not Sure">Not Sure</option>
                </select>
              </div>
              <div>
                <label htmlFor="req-make" className="block text-sm font-semibold text-gray-700 mb-2">
                  Preferred Make
                </label>
                <input
                  type="text"
                  id="req-make"
                  name="make"
                  value={formData.make}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="e.g., Toyota, Honda, Ford"
                />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="req-model" className="block text-sm font-semibold text-gray-700 mb-2">
                Preferred Model (Optional)
              </label>
              <input
                type="text"
                id="req-model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                placeholder="e.g., RAV4, CR-V, F-150"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="req-yearMin" className="block text-sm font-semibold text-gray-700 mb-2">
                  Year Range (Min)
                </label>
                <input
                  type="number"
                  id="req-yearMin"
                  name="yearMin"
                  value={formData.yearMin}
                  onChange={handleChange}
                  min="2000"
                  max="2025"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="2015"
                />
              </div>
              <div>
                <label htmlFor="req-yearMax" className="block text-sm font-semibold text-gray-700 mb-2">
                  Year Range (Max)
                </label>
                <input
                  type="number"
                  id="req-yearMax"
                  name="yearMax"
                  value={formData.yearMax}
                  onChange={handleChange}
                  min="2000"
                  max="2025"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="2022"
                />
              </div>
            </div>
          </div>

          {/* Budget & Timeline */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-money-dollar-circle-line text-amber-500" aria-hidden="true"></i>
              Budget &amp; Timeline
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="req-budgetMin" className="block text-sm font-semibold text-gray-700 mb-2">
                  Budget Min <span aria-hidden="true">*</span>
                </label>
                <input
                  type="number"
                  id="req-budgetMin"
                  name="budgetMin"
                  value={formData.budgetMin}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  min="0"
                  step="1000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="10000"
                />
              </div>
              <div>
                <label htmlFor="req-budgetMax" className="block text-sm font-semibold text-gray-700 mb-2">
                  Budget Max <span aria-hidden="true">*</span>
                </label>
                <input
                  type="number"
                  id="req-budgetMax"
                  name="budgetMax"
                  value={formData.budgetMax}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  min="0"
                  step="1000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="25000"
                />
              </div>
            </div>
            <div>
              <label htmlFor="req-timeline" className="block text-sm font-semibold text-gray-700 mb-2">
                When Do You Need It? <span aria-hidden="true">*</span>
              </label>
              <select
                id="req-timeline"
                name="timeline"
                value={formData.timeline}
                onChange={handleChange}
                required
                aria-required="true"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              >
                <option value="">Select timeline...</option>
                <option value="ASAP">ASAP (Within 1 week)</option>
                <option value="1-2 weeks">1-2 weeks</option>
                <option value="2-4 weeks">2-4 weeks</option>
                <option value="1-2 months">1-2 months</option>
                <option value="Just browsing">Just browsing</option>
              </select>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-file-list-line text-amber-500" aria-hidden="true"></i>
              Additional Details
            </h3>
            <label htmlFor="req-notes" className="block text-sm font-semibold text-gray-700 mb-2">
              Anything else we should know?
            </label>
            <textarea
              id="req-notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={5}
              maxLength={500}
              aria-describedby="req-notes-count"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
              placeholder="e.g., Must have AWD, prefer leather seats, need third row seating, etc."
            ></textarea>
            <div className="text-xs text-gray-500 mt-1 text-right">
              <span id="req-notes-count" aria-live="polite">{formData.notes.length}/500 characters</span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill" aria-hidden="true"></i>
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}